/**
 * Compass geometry, validation and trajectory-path maths.
 *
 * Compass semantics (frozen in CONTRACT.md):
 * - `x` is the economic axis: -10 socialism (left) .. +10 free market (right).
 * - `y` is the social axis:    -10 libertarian (bottom) .. +10 authoritarian (top).
 * - SVG pixels grow downward, so the `y` coordinate is inverted when projected.
 */

import { COMPASS_MAX, COMPASS_MIN } from './types'
import type { ChartGeometry, Entry, Point2D } from './types'

/** Ticks drawn along both axes, in compass units. */
const AXIS_TICKS: readonly number[] = [-10, -5, 0, 5, 10]

/** Number of compass units between the origin and the edge of the plot. */
const COMPASS_HALF_RANGE = 10

/** Coordinates are displayed, stored and shared at two decimals. */
const COORD_SCALE = 100

/**
 * Tension of the trajectory's cardinal spline: the single knob for how much the
 * curve bows through each point.
 *
 * The tangent at a point is normally the average of its neighbours' positions,
 * i.e. the direction of travel through it; scaling that by `t` sets how far the
 * control points sit from the chord:
 *
 * - `t = 1` is plain uniform Catmull-Rom. It is already C1-continuous (no kink),
 *   but with only a handful of points it reads as a polyline with rounded
 *   corners: the segments between points barely bow.
 * - `t > 1` exaggerates the tangents, so every turn becomes a visible arc. A
 *   sharp reversal loops instead of kinking.
 * - `t = 0` degenerates to straight segments between consecutive points.
 *
 * 1.35 was chosen by eye against real trajectories: the turns clearly sweep
 * without a reversal tangling into a knot. Note that a segment between two
 * points with a consistent direction is straight at any tension — there is
 * nothing in the data to bow it — so the visible curvature comes from the turns.
 *
 * The curve still passes exactly through every plotted point at any `t` — only
 * the tangents change — so the tension never moves a dot.
 */
export const TRAJECTORY_TENSION = 1.35

/**
 * Build the square layout for a chart.
 *
 * The inner plot is a square of `size - 2 * padding` centred in a `size` square
 * SVG, so compass origin (0, 0) always lands on the SVG centre.
 *
 * @param size Full side length of the square SVG, in SVG user units.
 * @param padding Gap between the SVG edge and the plot area.
 */
export function createGeometry(size: number, padding: number): ChartGeometry {
  const plot = size - 2 * padding
  return {
    size,
    plot,
    padding,
    center: { x: size / 2, y: size / 2 },
    scale: plot / (2 * COMPASS_HALF_RANGE),
  }
}

/**
 * Project a compass coordinate onto SVG pixels.
 *
 * (0, 0) maps to the plot centre; (-10, -10) to the bottom-left corner of the
 * plot; (10, 10) to the top-right. The `y` axis is inverted so that positive
 * (authoritarian) values appear ABOVE the centre, because SVG y grows downward.
 */
export function toPixel(x: number, y: number, geo: ChartGeometry): Point2D {
  return {
    x: geo.center.x + (x / COMPASS_HALF_RANGE) * (geo.plot / 2),
    y: geo.center.y - (y / COMPASS_HALF_RANGE) * (geo.plot / 2),
  }
}

/**
 * Inverse of {@link toPixel}: map an SVG pixel back to compass coordinates.
 *
 * The `y` axis is un-inverted, so a pixel above the centre (a smaller `y`) gives
 * a positive (authoritarian) value. Nothing is rounded and nothing is clamped
 * here: the raw value is returned, overshoot and all, because the caller decides
 * whether to clamp it (a tap) or discard it.
 */
export function fromPixel(pixel: Point2D, geo: ChartGeometry): Point2D {
  return {
    x: ((pixel.x - geo.center.x) / (geo.plot / 2)) * COMPASS_HALF_RANGE,
    y: ((geo.center.y - pixel.y) / (geo.plot / 2)) * COMPASS_HALF_RANGE,
  }
}

/**
 * Clamp a coordinate into the legal compass range `[-10, 10]`.
 *
 * Non-finite input (NaN, ±Infinity) has no meaningful position on the compass
 * and is treated as the origin, 0.
 */
export function clampCoord(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.min(COMPASS_MAX, Math.max(COMPASS_MIN, value))
}

/** True when `value` is a finite number inside the compass range. */
export function isValidCoord(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= COMPASS_MIN &&
    value <= COMPASS_MAX
  )
}

/** True when `value` is a whole year inside the supported range 1000..2999. */
export function isValidYear(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1000 && value <= 2999
}

/**
 * Return a new array sorted by year ascending.
 *
 * Stable: entries sharing a year keep their original relative order, which
 * keeps same-year dots in the order the user entered them. The input array is
 * never mutated.
 */
export function sortChronologically(entries: Entry[]): Entry[] {
  return [...entries].sort((a, b) => a.year - b.year)
}

/** Tick values drawn along both axes, in compass units. */
export function axisTicks(): number[] {
  return [...AXIS_TICKS]
}

/**
 * Format a single compass coordinate for display.
 *
 * At most two decimals, trailing zeros stripped, so `4` reads `4`, `4.5` reads
 * `4.5` and `3.14159` reads `3.14`. Extra decimals are truncated, not rounded,
 * except that the value is first normalised at six decimals so a binary
 * rounding artefact (such as `0.29 * 100 === 28.999999999999996`) does not drop
 * a legitimate hundredth. Negative zero renders as `0`. This is the only
 * coordinate formatter in the app: the entry list, the chart tooltip, the point
 * `aria-label` and the exported legend all go through it, so the same entry
 * reads identically everywhere.
 */
export function formatValue(value: number): string {
  // Round the scaled value to six decimals first: `0.29 * 100` is
  // 28.999999999999996 in binary floating point, and truncating that raw would
  // silently drop a legitimate hundredth.
  const scaled = Math.trunc(Number((value * 100).toFixed(6)))
  // `String(-0)` is already `'0'`, so a truncated negative under 0.01 renders 0.
  return String(scaled / 100)
}

/**
 * Format a compass point as `'x -7.5, y -2'`, using {@link formatValue} for both
 * coordinates.
 */
export function formatCoords(x: number, y: number): string {
  return `x ${formatValue(x)}, y ${formatValue(y)}`
}

/**
 * Round a coordinate to the two decimals the app displays, stores and shares.
 *
 * The single rounding used by the store and by the row inputs, so a legacy or
 * hand-edited value, a typed value and a shared value all end up identical.
 * `-0` folds to `0`: the two serialise to the same JSON but are not the same
 * value to `Object.is`, which would make an entry seeded with `-0.004` compare
 * unequal to `0`.
 */
export function roundCoord(value: number): number {
  const rounded = Math.round(value * COORD_SCALE) / COORD_SCALE
  return rounded === 0 ? 0 : rounded
}

/**
 * Snap a coordinate to the nearest multiple of `step`, then clamp it into
 * `[-10, 10]`.
 *
 * This is what turns a tap or a drag into a grid coordinate: a placement step of
 * `0.5` lands an entry on a half unit. The nearest multiple is clamped after
 * snapping (`9.9` snaps to `10`, `-12` to `-10`). A non-positive or non-finite
 * `step` has no multiples to snap to, so the clamped value is returned unchanged
 * rather than dividing by zero. The result is rounded to the two decimals the
 * app stores, so a binary artefact such as `0.1 + 0.2` cannot reach an entry,
 * and `-0` folds to `0`.
 */
export function snapCoord(value: number, step: number): number {
  const clamped = clampCoord(value)
  if (!Number.isFinite(step) || step <= 0) return Object.is(clamped, -0) ? 0 : clamped
  return roundCoord(clampCoord(Math.round(value / step) * step))
}

/** Format a point as an SVG `x y` pair with two-decimal rounding. */
function formatPoint(point: Point2D): string {
  return `${roundCoord(point.x)} ${roundCoord(point.y)}`
}

/**
 * One cubic-Bézier segment of the trajectory, in SVG pixels.
 *
 * `start` and `end` are consecutive plotted points, so the curve passes exactly
 * through both; the two control points are the cardinal-spline tangents at the
 * ends, scaled by {@link TRAJECTORY_TENSION}. Together they fully determine the
 * segment, which is what lets the chart serialise and sample the same curve
 * without a second copy of the maths.
 */
export interface TrajectorySegment {
  start: Point2D
  control1: Point2D
  control2: Point2D
  end: Point2D
}

/**
 * The trajectory through `entries`, in the given order (callers pass
 * `sortChronologically(entries)`), as cubic-Bézier segments in SVG pixels.
 *
 * This is the one definition of the curve: {@link trajectoryPath} serialises
 * exactly these segments, and the chart's arrowhead samples them to find the
 * direction it covers, so the drawn line and the direction marker can never
 * disagree about where the curve goes. For segment `i` the control points are
 * `p[i] + t * (p[i+1] - p[i-1]) / 6` and
 * `p[i+1] - t * (p[i+2] - p[i]) / 6`, with the neighbour indices clamped at the
 * ends — which is why a segment between points with a consistent direction
 * comes out straight, and why the last control point always lies on the final
 * chord.
 *
 * `[]` for fewer than two entries: with no pair of points there is no segment.
 */
export function trajectorySegments(entries: Entry[], geo: ChartGeometry): TrajectorySegment[] {
  const points = entries.map((entry) => toPixel(entry.x, entry.y, geo))
  const segments: TrajectorySegment[] = []

  for (let i = 0; i + 1 < points.length; i += 1) {
    const previous = i > 0 ? points[i - 1] : points[i]
    const start = points[i]
    const end = points[i + 1]
    const next = i + 2 < points.length ? points[i + 2] : end

    segments.push({
      start,
      control1: {
        x: start.x + (TRAJECTORY_TENSION * (end.x - previous.x)) / 6,
        y: start.y + (TRAJECTORY_TENSION * (end.y - previous.y)) / 6,
      },
      control2: {
        x: end.x - (TRAJECTORY_TENSION * (next.x - start.x)) / 6,
        y: end.y - (TRAJECTORY_TENSION * (next.y - start.y)) / 6,
      },
      end,
    })
  }

  return segments
}

/**
 * The point on `segment` at parameter `t`: `t = 0` is `start`, `t = 1` is
 * `end`, and the values between are the standard cubic-Bézier blend. `t` is not
 * arc length — a cubic does not travel at constant speed — so callers that need
 * a position by distance along the curve accumulate chord lengths (see the
 * chart's arrowhead).
 */
export function cubicPointAt(segment: TrajectorySegment, t: number): Point2D {
  const u = 1 - t
  const a = u * u * u
  const b = 3 * u * u * t
  const c = 3 * u * t * t
  const d = t * t * t
  return {
    x: a * segment.start.x + b * segment.control1.x + c * segment.control2.x + d * segment.end.x,
    y: a * segment.start.y + b * segment.control1.y + c * segment.control2.y + d * segment.end.y,
  }
}

/**
 * Build the SVG path that traces the trajectory through `entries`, in the given
 * order (callers pass `sortChronologically(entries)`).
 *
 * - No entries: `''`.
 * - One entry: `M px py` — a dot with no line.
 * - Two or more: a smooth cubic-Bézier curve that passes through EVERY pixel
 *   point, a cardinal spline built from the uniform Catmull-Rom tangents scaled
 *   by {@link TRAJECTORY_TENSION}. The endpoints are untouched, so the curve
 *   still hits every point exactly at any `t`; `t = 1` reproduces the old
 *   uniform Catmull-Rom curve and `t = 0` collapses each segment to a straight
 *   line. Commands are joined with single spaces and numbers rounded to two
 *   decimals.
 *
 * The segments come from {@link trajectorySegments}, so this serialiser holds
 * no geometry of its own and cannot drift from anything sampling the curve.
 */
export function trajectoryPath(entries: Entry[], geo: ChartGeometry): string {
  if (entries.length === 0) return ''

  const segments = trajectorySegments(entries, geo)
  if (segments.length === 0) return `M ${formatPoint(toPixel(entries[0].x, entries[0].y, geo))}`

  const commands: string[] = [`M ${formatPoint(segments[0].start)}`]
  for (const segment of segments) {
    commands.push(
      `C ${formatPoint(segment.control1)} ${formatPoint(segment.control2)} ${formatPoint(segment.end)}`,
    )
  }

  return commands.join(' ')
}
